import React, { Component } from 'react';
import FaqSection from './FaqSection';
import './mainpage.css';
import './bioland.css';
import logo from './logo.png';
import msAppSource from './assets/MS_AppSource.webp';
import { Button } from '@fluentui/react-components';
import { 
  Navigation24Regular,
  MailFilled,
  SearchFilled,
  SearchInfoFilled,
  OrganizationFilled,
  PeopleFilled,
  ScalesFilled,
  CheckmarkCircleFilled
} from '@fluentui/react-icons';
import NavBar from './NavBar';

const MARKETPLACE_OFFER_URL = 'https://marketplace.microsoft.com/en-us/product/azure-application/psarakisantonios1727862143801.meta-365-pentities-ent?tab=Overview';

const AZURE_NOTE = 'Azure services (storage, AI, document intelligence) are billed separately through your Azure subscription. Electronic signing powered by third-party providers may incur additional costs.';

interface PlanFeature {
  label: string;
  href?: string;
}

interface PlanLimit {
  dimension: string;
  included: string;
  overage: string;
}

interface MarketplacePlan {
  name: string;
  badge?: string;
  tagline: string;
  price: string;
  /** Hide the price and show a Contact Sales call to action instead. */
  contactSales?: boolean;
  /** Azure Portal create link for this specific plan. */
  offerUrl?: string;
  accent: string;
  dark?: boolean;
  features: PlanFeature[];
  limits: PlanLimit[];
  note: string;
}

/**
 * Mirrors the plans published on the Microsoft Marketplace offer
 * (psarakisantonios1727862143801.meta-365-pentities-ent-preview).
 * Keep names, prices and metered dimensions in sync with the offer.
 */
const MARKETPLACE_PLANS: MarketplacePlan[] = [
  {
    name: 'Starter',
    offerUrl: 'https://portal.azure.com/#create/psarakisantonios1727862143801.meta-365-pentities-entstarter',
    badge: 'FREE',
    tagline: 'Entry-level AI-powered operations for small teams and pilot deployments.',
    price: '$0.00',
    accent: '#0d9488',
    features: [
      { label: 'Entity management (contracts, records, tickets, projects)' },
      { label: 'Document Management' },
      { label: 'Email Management' },
      { label: 'Azure AI Document Intelligence' },
      { label: 'Workflow Automation', href: '/solutions/automation' },
      { label: 'Microsoft Teams integration' },
      { label: 'Deployed in your own Azure environment' },
      { label: 'Email support' }
    ],
    limits: [
      { dimension: 'Active Users', included: '5', overage: '$10.00 / tenant' },
      { dimension: 'Active Workflows', included: '3', overage: '$3.60 / user' }
    ],
    note: AZURE_NOTE
  },
  {
    name: 'HR Operations',
    offerUrl: 'https://portal.azure.com/#create/psarakisantonios1727862143801.meta-365-pentities-enthr-operation',
    tagline: 'Complete employee lifecycle management inside Microsoft Teams.',
    price: '$490.00',
    accent: '#0078d4',
    features: [
      { label: 'Employee lifecycle management', href: '/solutions/hr-template' },
      { label: 'HR document & email management' },
      { label: 'Leave & absence management' },
      { label: 'Training & compliance tracking' },
      { label: 'HR financial management' },
      { label: 'AI-powered HR assistant' },
      { label: 'Azure AI Document Intelligence' },
      { label: 'Workflow Automation', href: '/solutions/automation' },
      { label: 'Microsoft 365 integration (Teams, SharePoint, Exchange, Planner)' }
    ],
    limits: [
      { dimension: 'HR Users', included: 'Unlimited', overage: '$0.00 / user' },
      { dimension: 'Active Users', included: '22', overage: '$9.00 / tenant' },
      { dimension: 'Active Workflows', included: '15', overage: '$3.60 / user' }
    ],
    note: AZURE_NOTE
  },
  {
    name: 'Legal Operations',
    offerUrl: 'https://portal.azure.com/#create/psarakisantonios1727862143801.meta-365-pentities-entlegal-operations',
    tagline: 'Matters, contracts and legal exposure managed from one workspace.',
    price: '$780.00',
    accent: '#7c3aed',
    features: [
      { label: 'Matter & case management', href: '/solutions/case-management' },
      { label: 'Contract lifecycle management' },
      { label: 'Legal document & email management' },
      { label: 'Financial exposure & cost tracking' },
      { label: 'AI-powered legal assistant' },
      { label: 'Workflow Automation', href: '/solutions/automation' },
      { label: 'Full traceability for audits & reviews' }
    ],
    limits: [
      { dimension: 'Legal cases', included: 'Unlimited', overage: '$0.00 / case' },
      { dimension: 'Active Users', included: '50', overage: '$12.00 / tenant' },
      { dimension: 'Active Workflows', included: '25', overage: '$3.60 / user' }
    ],
    note: AZURE_NOTE
  },
  {
    name: 'Service Desk',
    offerUrl: 'https://portal.azure.com/#create/psarakisantonios1727862143801.meta-365-pentities-entservice-desk',
    tagline: 'Modern service management for IT, HR, Finance, Legal and Facilities.',
    price: '$690.00',
    accent: '#ea580c',
    features: [
      { label: 'Centralized ticket management', href: '/solutions/ticketing' },
      { label: 'Multi-department service management' },
      { label: 'Service Level Agreement (SLA) management' },
      { label: 'AI-powered service assistant' },
      { label: 'Intelligent knowledge management', href: '/knowledge' },
      { label: 'Workflow Automation', href: '/solutions/automation' },
      { label: 'Automated routing, approvals & escalations' }
    ],
    limits: [
      { dimension: 'Active Users', included: '100', overage: '$12.00 / tenant' },
      { dimension: 'Tickets', included: 'Unlimited', overage: '$0.00 / ticket' },
      { dimension: 'Active Workflows', included: '15', overage: '$3.60 / user' }
    ],
    note: AZURE_NOTE
  },
  {
    name: 'Business Operations',
    offerUrl: 'https://portal.azure.com/#create/psarakisantonios1727862143801.meta-365-pentities-entbusiness-operations',
    badge: 'MOST POPULAR',
    tagline: 'HR, Finance, Ticketing and documents unified for mid-sized organizations.',
    price: '$1,900.00',
    accent: '#0f766e',
    features: [
      { label: 'Human Resources management', href: '/solutions/hr-template' },
      { label: 'Financial operations management' },
      { label: 'Service request & ticket management', href: '/solutions/ticketing' },
      { label: 'Document and email management' },
      { label: 'Azure AI Document Intelligence' },
      { label: 'Workflow Automation', href: '/solutions/automation' },
      { label: 'Cross-department collaboration' }
    ],
    limits: [
      { dimension: 'HR Users', included: 'Unlimited', overage: '$0.00 / user' },
      { dimension: 'Active Users', included: '100', overage: '$12.00 / tenant' },
      { dimension: 'Tickets', included: 'Unlimited', overage: '$0.00 / ticket' },
      { dimension: 'Active Workflows', included: '50', overage: '$3.60 / user' }
    ],
    note: AZURE_NOTE
  },
  {
    name: 'Enterprise Automation',
    badge: 'ENTERPRISE',
    tagline: 'Every module, enterprise-scale automation and governance in one platform.',
    price: '$3,000.00',
    contactSales: true,
    accent: '#a0aec0',
    dark: true,
    features: [
      { label: 'Document Management' },
      { label: 'Email Management' },
      { label: 'Human Resources Management', href: '/solutions/hr-template' },
      { label: 'Legal Operations Management', href: '/solutions/case-management' },
      { label: 'Financial Management' },
      { label: 'Service Desk & Ticketing', href: '/solutions/ticketing' },
      { label: 'Azure AI Document Intelligence' },
      { label: 'Custom AI Agents' },
      { label: 'Enterprise Workflow Automation', href: '/solutions/automation' },
      { label: 'Teams & SharePoint integration' },
      { label: 'Reporting & analytics' },
      { label: 'Governance & compliance controls' }
    ],
    limits: [
      { dimension: 'HR Users', included: 'Unlimited', overage: '$0.00 / user' },
      { dimension: 'Active Users', included: '1,000', overage: '$10.00 / tenant' },
      { dimension: 'Active Workflows', included: 'Unlimited', overage: '$0.00 / user' },
      { dimension: 'Legal cases', included: 'Unlimited', overage: '$0.00 / case' },
      { dimension: 'Tickets', included: 'Unlimited', overage: '$0.00 / ticket' }
    ],
    note: 'Azure services billed separately. Contact sales for custom SLA and dedicated enterprise support.'
  }
];

interface PlansPageProps {}

interface PlansPageState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
}

class PlansAndPricing extends Component<PlansPageProps, PlansPageState> {
  constructor(props: PlansPageProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false
    };
  }

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  toggleSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  };

  closeSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: false });
  };

  render() {
    const { isMenuOpen } = this.state;

    return (
      <div className="main-page plans-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="bioland-hero media-hero">
          <div className="media-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="media-hero-video">
              <source src={require('./assets/video/web/12721926-hd_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="container">
            <div className="bioland-hero-inner">
              <span className="bioland-label">Plans &amp; Pricing</span>
              <h1 className="bioland-hero-title">
                Choose the plan that fits your organization
              </h1>
              <div className="bioland-meta">
                <span>Core features in every plan</span>
                <span className="bioland-meta-dot">•</span>
                <span>Runs in your own Microsoft 365 tenant</span>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="pricing-section" style={{ padding: '60px 20px', backgroundColor: '#f9f9f9' }}>
          <div className="container">
            <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '24px', maxWidth: '1350px', margin: '0 auto', alignItems: 'stretch' }}>
              {MARKETPLACE_PLANS.map(plan => {
                const dark = !!plan.dark;
                const accent = plan.accent;
                const muted = dark ? 'rgba(255,255,255,0.7)' : '#666';
                const rule = dark ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f0f0f0';
                const headColor = dark ? 'rgba(255,255,255,0.6)' : accent;
                const headRule = dark ? '1px solid rgba(255,255,255,0.15)' : '1px solid ' + accent;
                const noteBorder = dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid ' + accent + '40';
                return (
                  <div
                    key={plan.name}
                    className="pricing-card"
                    style={{
                      backgroundColor: dark ? '#1a1a2e' : 'white',
                      color: dark ? 'white' : 'inherit',
                      padding: '35px 25px',
                      borderRadius: '8px',
                      boxShadow: dark ? '0 4px 16px rgba(0,0,0,0.25)' : '0 1px 3px rgba(0,0,0,0.05)',
                      border: '2px solid ' + (dark ? '#16213e' : accent),
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {plan.badge && (
                      <div style={{ backgroundColor: dark ? 'rgba(255,255,255,0.08)' : accent + '1a', padding: '4px 10px', borderRadius: '4px', display: 'inline-block', width: 'fit-content', marginBottom: '12px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '0.5px', color: dark ? '#a0aec0' : accent }}>
                        {plan.badge}
                      </div>
                    )}
                    <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: dark ? 'white' : accent }}>{plan.name}</h3>
                    <p style={{ color: muted, fontSize: '14px', marginBottom: '25px', minHeight: '40px' }}>{plan.tagline}</p>
                    <div style={{ marginBottom: '24px' }}>
                      {plan.contactSales ? (
                        <span style={{ fontSize: '26px', fontWeight: 'bold', color: dark ? 'white' : accent }}>Contact Sales</span>
                      ) : (
                        <>
                          <span style={{ fontSize: '32px', fontWeight: 'bold', color: dark ? 'white' : accent }}>{plan.price}</span>
                          <span style={{ color: muted, fontSize: '14px', marginLeft: '8px' }}>/month</span>
                        </>
                      )}
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, marginBottom: '20px', fontSize: '13px' }}>
                      <li style={{ padding: '6px 0 4px 0', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: dark ? 'rgba(255,255,255,0.5)' : accent }}>
                        Includes
                      </li>
                      {plan.features.map(feature => (
                        <li key={feature.label} style={{ padding: '5px 0', display: 'flex', alignItems: 'flex-start' }}>
                          <CheckmarkCircleFilled style={{ color: dark ? '#a0aec0' : accent, marginRight: '10px', flexShrink: 0, marginTop: '2px' }} />
                          {feature.href
                            ? <a href={feature.href} style={{ color: dark ? 'rgba(255,255,255,0.8)' : '#0078d4', textDecoration: 'underline' }}>{feature.label}</a>
                            : <span>{feature.label}</span>}
                        </li>
                      ))}
                    </ul>

                    {/* Usage Limits Table */}
                    <div style={{ marginBottom: '24px' }}>
                      <p style={{ fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: dark ? 'rgba(255,255,255,0.5)' : accent, marginBottom: '10px' }}>Usage Limits</p>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ backgroundColor: dark ? 'rgba(255,255,255,0.07)' : accent + '14' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: headRule, color: headColor, fontWeight: '600' }}>Dimension</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: headRule, color: headColor, fontWeight: '600' }}>Included</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: headRule, color: headColor, fontWeight: '600' }}>Additional</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.limits.map(limit => (
                            <tr key={limit.dimension}>
                              <td style={{ padding: '5px 8px', borderBottom: rule, color: dark ? 'rgba(255,255,255,0.8)' : '#333' }}>{limit.dimension}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'left', borderBottom: rule, fontWeight: '600', color: dark ? '#a0aec0' : 'inherit' }}>{limit.included}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'left', borderBottom: rule, color: dark ? 'rgba(255,255,255,0.45)' : '#666' }}>{limit.overage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Disclaimer */}
                    <div style={{ backgroundColor: dark ? 'rgba(255,255,255,0.05)' : accent + '0f', border: noteBorder, borderRadius: '6px', padding: '12px 14px', marginBottom: '20px' }}>
                      <p style={{ fontWeight: '700', fontSize: '11px', color: dark ? 'rgba(255,255,255,0.6)' : accent, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Important Notes</p>
                      <p style={{ fontSize: '11px', color: dark ? 'rgba(255,255,255,0.5)' : '#444', lineHeight: '1.5', margin: 0 }}>{plan.note}</p>
                    </div>

                    {plan.contactSales ? (
                      <a
                        href="/contactus"
                        style={{ display: 'flex', justifyContent: 'center', marginTop: 'auto', textDecoration: 'none' }}
                      >
                        <div style={{ backgroundColor: dark ? 'white' : accent, color: dark ? '#1a1a2e' : 'white', fontWeight: '700', fontSize: '14px', padding: '12px 28px', borderRadius: '6px', cursor: 'pointer', letterSpacing: '0.3px' }}>Contact Sales</div>
                      </a>
                    ) : (
                    <a
                      href={plan.offerUrl || MARKETPLACE_OFFER_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', justifyContent: 'center', marginTop: 'auto' }}
                    >
                      <img loading="lazy" decoding="async" src={msAppSource} alt="Get it from Microsoft AppSource" style={{ height: '52px', width: 'auto', cursor: 'pointer', transition: 'transform 0.2s, opacity 0.2s' }} onMouseOver={e => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.05)'; (e.currentTarget as HTMLImageElement).style.opacity = '0.9'; }} onMouseOut={e => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLImageElement).style.opacity = '1'; }} />
                    </a>
                    )}
                  </div>
                );
              })}
            </div>

            {/* General marketplace offer link */}
            <div style={{ maxWidth: '900px', margin: '36px auto 0', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', margin: 0 }}>
                Prefer to compare everything in one place? View the complete <strong>pEntities Enterprise</strong> offer on the Microsoft Marketplace to see all plans, pricing and metered dimensions side by side, then subscribe to the one that fits your organisation.
              </p>
              <a
                href={MARKETPLACE_OFFER_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-block', marginTop: '14px', color: '#0078d4', fontWeight: '600', fontSize: '14px', textDecoration: 'underline' }}
              >
                View the full offer on Microsoft Marketplace
              </a>
            </div>
          </div>
        </section>

        {/* Contact Sales Note */}
        <section style={{ padding: '30px 20px 10px' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ backgroundColor: '#f0f7ff', border: '1px solid #d0e4f7', borderRadius: '10px', padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <p style={{ fontWeight: '700', fontSize: '16px', color: '#0078d4', marginBottom: '4px' }}>Need unlimited usage?</p>
                <p style={{ color: '#444', fontSize: '14px', margin: 0 }}>For custom limits, unlimited entities, users or workflows contact our sales team for a tailored Enterprise quote.</p>
              </div>
              <a
                href="/contactus"
                style={{ display: 'inline-block', backgroundColor: '#0078d4', color: 'white', fontWeight: '600', fontSize: '14px', padding: '10px 24px', borderRadius: '6px', textDecoration: 'none', whiteSpace: 'nowrap', transition: 'background-color 0.2s' }}
                onMouseOver={e => (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#005fa3'}
                onMouseOut={e => (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#0078d4'}
              >
                Contact Sales
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section style={{ padding: '60px 20px' }}>
          <div className="container" style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'left' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '40px', textAlign: 'center' }}>Frequently Asked Questions</h2>
            
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>How do I get started?</h4>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                Start by downloading and installing the <strong>free Starter plan</strong> directly from <a href="https://marketplace.microsoft.com/en-us/product/WA200009732" target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4' }}>Microsoft AppSource</a> no credit card required. Once installed, you can upgrade to a premium plan (HR Operations, Legal Operations, Service Desk, Business Operations, or Enterprise Automation) online at any time either <strong>from within the app</strong> or by selecting the corresponding <strong>Azure Marketplace managed application offer</strong> for instant access.
              </p>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>Can I change my plan anytime?</h4>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect at your next billing cycle. No setup fees or long-term commitments required.
              </p>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>Do you offer discounts for annual billing?</h4>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                Yes! We offer 20% discount for annual subscriptions on all plans. Contact our sales team for more details about volume discounts.
              </p>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>What's included in support?</h4>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                All plans include email support. <strong>Business Operations</strong> and <strong>Enterprise Automation</strong> plans add priority support with faster response times and phone support. Enterprise customers can also arrange a custom SLA and dedicated support contact your sales team for details.
              </p>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>Can I get a custom plan?</h4>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                Absolutely! Our Enterprise Automation plan is fully customizable to meet your organization's specific needs. Contact our sales team to discuss your requirements.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ padding: '60px 20px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
          <div className="container">
            <h2 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '20px' }}>Ready to Transform Your Business?</h2>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '30px' }}>
              Start your journey with Meta-365 today. Choose your plan and unlock powerful productivity solutions.
            </p>
            <Button 
              className="btn-secondary-large"
              appearance="primary"
              size="medium"
              onClick={() => window.open('https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled', '_blank')}
            >
              Schedule a Demo
            </Button>
          </div>
        </section>

        {/* Footer */}
        <FaqSection path="/plans-pricing" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default PlansAndPricing;

